<template>
  <v-row>
    <!-- settings + context -->
    <v-col cols="12" md="4">
      <v-card outlined class="mb-4">
        <v-card-title class="text-subtitle-1">AI provider</v-card-title>
        <v-card-text>
          <v-select v-model="settings.provider" :items="providerItems" outlined dense label="How should the assistant think?" @change="onProviderChange" />

          <template v-if="settings.provider !== 'manual'">
            <v-combobox v-model="settings.model" autocomplete="off" name="rb-ai-model" data-lpignore="true" data-1p-ignore="true" :search-input.sync="modelTyped" :items="modelSuggestions" outlined dense label="Model" :hint="`Default: ${defaults.model || ''}`" persistent-hint />
            <!-- plain text field masked with CSS: a real type="password" field makes the browser offer saved logins
                 (and fill the Model box above it with a username) -->
            <v-text-field
              v-if="needsKey" v-model="apiKey" class="mt-4" :class="{ 'rb-secret': !showKey }" outlined dense label="API key" type="text"
              :append-icon="showKey ? 'mdi-eye-off' : 'mdi-eye'" autocomplete="off" name="rb-ai-service-key" spellcheck="false"
              data-lpignore="true" data-1p-ignore="true" data-form-type="other" @click:append="showKey = !showKey"
            />
            <v-checkbox v-if="needsKey" v-model="settings.remember" dense hide-details label="Remember the key in this browser" />
            <div v-if="needsKey" class="text-caption grey--text mb-2">
              The key stays in your browser (never saved to the database or in exports) and is sent only with each request to the AI service.
            </div>
            <v-text-field
              v-if="settings.provider !== 'anthropic'" v-model="settings.baseUrl" class="mt-2" outlined dense
              :label="settings.provider === 'local' ? 'Server address (as seen by the app server)' : 'API address (optional)'"
              :placeholder="defaults.baseUrl"
            />
            <div v-if="settings.provider === 'local'" class="text-caption grey--text mb-2">
              Free: install Ollama or LM Studio, pull a model, and point this at it. Larger models write better SQL.
            </div>
            <v-btn small outlined :loading="testing" :disabled="needsKey && !apiKey" @click="testConnection"><v-icon left small>mdi-lan-connect</v-icon>Test connection</v-btn>
          </template>
          <div v-else class="text-caption grey--text">
            No key and no cost: the assistant builds the full prompt for you to paste into any chat (Claude, ChatGPT, Gemini...), then you paste the answer back.
          </div>
        </v-card-text>
      </v-card>

      <v-card outlined>
        <v-card-title class="text-subtitle-1">
          Assistant context
          <v-chip v-if="ctx" small class="ml-2" :color="!ctx.built ? 'warning' : ctx.stale ? 'warning' : 'success'">
            {{ !ctx.built ? 'not built' : ctx.stale ? 'out of date' : 'up to date' }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <div class="text-caption mb-2">
            What the assistant already knows about the system (database tables, business rules, report format, shared SQL blocks, example reports).
            It is built once and reused, so each request only carries your instruction.
          </div>
          <div v-if="ctx && ctx.built" class="text-caption">
            Built {{ new Date(ctx.builtAt).toLocaleString() }} - {{ ctx.stats.models }} tables, about {{ ctx.stats.approxTokens.toLocaleString() }} tokens
          </div>
          <v-alert v-if="ctx && ctx.stale" type="warning" dense text class="mt-2">
            The system changed since this was built (database tables, rules or example reports). Rebuild so the assistant works from the current picture.
          </v-alert>
          <v-btn small color="primary" class="mt-3" :loading="rebuilding" @click="rebuild">
            <v-icon left small>mdi-database-refresh-outline</v-icon>{{ ctx && ctx.built ? 'Rebuild context' : 'Build context' }}
          </v-btn>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- conversation -->
    <v-col cols="12" md="8">
      <v-card outlined class="mb-4">
        <v-card-text>
          <v-btn-toggle v-model="mode" mandatory dense class="mb-3">
            <v-btn small value="edit"><v-icon left small>mdi-pencil-outline</v-icon>Change this report</v-btn>
            <v-btn small value="new"><v-icon left small>mdi-file-plus-outline</v-icon>Start a new report</v-btn>
          </v-btn-toggle>
          <v-textarea
            v-model="instruction" outlined rows="4" auto-grow counter="8000" hide-details="auto"
            :label="mode === 'edit' ? 'What should change? e.g. \'add a salesperson filter and a chart of sales by day\'' : 'Describe the report. e.g. \'monthly returns by reason with the value refunded and a list of the returns behind it\''"
          />
          <div v-if="history.length" class="mt-2">
            <span class="text-caption grey--text mr-2">Earlier:</span>
            <v-chip v-for="(h, i) in history.slice(0, 4)" :key="i" x-small outlined class="mr-1 mb-1" @click="instruction = h">{{ h.slice(0, 40) }}{{ h.length > 40 ? '...' : '' }}</v-chip>
          </div>
          <div class="mt-3 d-flex align-center flex-wrap">
            <template v-if="settings.provider !== 'manual'">
              <v-btn color="primary" :loading="generating" :disabled="!canGenerate" @click="generate"><v-icon left>mdi-creation</v-icon>Generate</v-btn>
              <span v-if="generating" class="ml-3 text-caption grey--text">Writing, checking the SQL and running it against your data - this can take a minute...</span>
            </template>
            <template v-else>
              <v-btn color="primary" :loading="promptLoading" :disabled="!instruction.trim() || !(ctx && ctx.built)" @click="buildPrompt"><v-icon left>mdi-content-copy</v-icon>Copy prompt</v-btn>
            </template>
            <span v-if="!(ctx && ctx.built)" class="ml-3 text-caption warning--text">Build the context first.</span>
          </div>

          <!-- copy / paste route -->
          <div v-if="settings.provider === 'manual' && promptText" class="mt-4">
            <v-alert type="success" dense text>
              Prompt copied ({{ promptTokens.toLocaleString() }} tokens approx). Paste it into your AI chat, then paste its answer below.
              <v-btn x-small text class="ml-2" @click="copy(promptText)">Copy again</v-btn>
            </v-alert>
            <v-textarea v-model="pasted" outlined rows="6" label="Paste the AI's answer here" hide-details />
            <v-btn class="mt-2" color="primary" :loading="reading" :disabled="!pasted.trim()" @click="readReply"><v-icon left>mdi-text-box-check-outline</v-icon>Read answer</v-btn>
          </div>
        </v-card-text>
      </v-card>

      <v-alert v-if="error" type="error" outlined>{{ error }}</v-alert>

      <!-- proposal -->
      <v-card v-if="proposal" outlined>
        <v-card-title class="text-subtitle-1">
          Proposal
          <v-chip small class="ml-3" :color="proposal.problems.length ? 'error' : 'success'">
            {{ proposal.problems.length ? `${proposal.problems.length} problem(s)` : proposal.ran ? 'valid and ran against your data' : 'valid' }}
          </v-chip>
          <v-chip v-if="proposal.attempts > 1" small class="ml-2" outlined>{{ proposal.attempts }} attempts</v-chip>
          <v-chip v-if="usageText" small class="ml-2" outlined>{{ usageText }}</v-chip>
        </v-card-title>
        <v-card-text>
          <div v-if="proposal.explanation" class="mb-3" style="white-space: pre-line">{{ proposal.explanation }}</div>
          <v-alert v-if="proposal.problems.length" type="error" dense text>
            <div v-for="(p, i) in proposal.problems" :key="i">- {{ p }}</div>
            <div class="mt-1 text-caption">You can still apply it and fix the details by hand, or ask the AI to correct them.</div>
          </v-alert>

          <div v-if="!changes.length" class="text-body-2 grey--text">The proposal is identical to the report in the editor.</div>
          <div v-else>
            <div class="d-flex align-center mb-2 flex-wrap">
              <b class="mr-3">{{ changes.length }} change{{ changes.length === 1 ? '' : 's' }}</b>
              <v-btn x-small text @click="accepted = changes.map((c) => c.id)">Select all</v-btn>
              <v-btn x-small text @click="accepted = []">Select none</v-btn>
            </div>
            <v-expansion-panels multiple accordion>
              <v-expansion-panel v-for="c in changes" :key="c.id">
                <v-expansion-panel-header>
                  <div class="d-flex align-center">
                    <v-checkbox v-model="accepted" :value="c.id" dense hide-details class="mt-0 pt-0 mr-2" @click.stop />
                    <v-chip x-small label :color="kindColor(c.kind)" class="mr-2" dark>{{ c.kind }}</v-chip>
                    <span>{{ c.title }}</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense>
                    <v-col v-if="c.before" cols="12" :md="c.after ? 6 : 12">
                      <div class="text-caption grey--text">Before</div>
                      <pre class="rb-diff">{{ c.before }}</pre>
                    </v-col>
                    <v-col v-if="c.after" cols="12" :md="c.before ? 6 : 12">
                      <div class="text-caption grey--text">After</div>
                      <pre class="rb-diff">{{ c.after }}</pre>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="discard">Discard</v-btn>
          <v-spacer />
          <v-btn outlined :disabled="!changes.length || !accepted.length" @click="apply(false)">Apply selected ({{ accepted.length }})</v-btn>
          <v-btn color="primary" :disabled="!proposal.template" @click="apply(true)">Apply all</v-btn>
        </v-card-actions>
        <v-card-text class="pt-0 text-caption grey--text">
          Applying only changes the editor - nothing is saved or published. Check the Preview and Tests tabs, then Save.
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { errorText } from '../rbUtil'
import axios from 'axios'
import { mapGetters } from 'vuex'
import { state } from './editorState'
import { diffTemplates, applyChanges } from './diffUtil'

const LS = 'rb:ai:settings'
const keyName = (p) => `rb:ai:key:${p}`
const sessionKeys = {} // keys the user chose not to remember live only until the page is reloaded

export default {
  name: 'RbAiTab',
  data () {
    return {
      providers: {}, ctx: null, rebuilding: false, testing: false,
      settings: { provider: 'anthropic', model: '', baseUrl: '', remember: true },
      apiKey: '', showKey: false, modelTyped: '',
      mode: 'edit', instruction: '', history: [],
      generating: false, error: '', proposal: null, changes: [], accepted: [],
      promptText: '', promptTokens: 0, promptLoading: false, pasted: '', reading: false
    }
  },
  computed: {
    ...mapGetters(['getEndpoint']),
    base () { return `${this.getEndpoint}/api/rb-builder` },
    providerItems () {
      return [
        ...Object.entries(this.providers).map(([value, p]) => ({ value, text: p.label })),
        { value: 'manual', text: 'Copy / paste into any AI chat (free, no key)' }
      ]
    },
    defaults () { return this.providers[this.settings.provider] || {} },
    needsKey () { return !!(this.defaults && this.defaults.needsKey) },
    modelSuggestions () {
      return ({
        anthropic: ['claude-sonnet-5-5', 'claude-opus-5-5', 'claude-haiku-4-5-20251001'],
        openai: ['gpt-4.1', 'gpt-4o', 'o4-mini'],
        gemini: ['gemini-3.8-flash', 'gemini-3.5-flash', 'gemini-2.5-pro'],
        local: ['llama3.1', 'qwen2.5-coder', 'deepseek-coder-v2', 'mistral']
      })[this.settings.provider] || []
    },
    canGenerate () {
      return this.instruction.trim() && this.ctx && this.ctx.built && (!this.needsKey || this.apiKey) && !(this.mode === 'edit' && !state.tpl)
    },
    usageText () {
      const u = this.proposal && this.proposal.usage
      if (!u || !(u.inputTokens || u.outputTokens)) return ''
      return `${(u.inputTokens || 0).toLocaleString()} in / ${(u.outputTokens || 0).toLocaleString()} out${u.cacheRead ? ` (${u.cacheRead.toLocaleString()} from cache)` : ''}`
    }
  },
  async created () {
    try {
      const saved = JSON.parse(localStorage.getItem(LS) || 'null')
      if (saved) Object.assign(this.settings, saved)
    } catch (e) { /* storage unavailable */ }
    this.loadKey()
    const res = await axios.get(`${this.base}/ai/providers`)
    this.providers = res.data.data
    await this.loadContext()
  },
  watch: {
    settings: { deep: true, handler () { this.saveSettings() } },
    apiKey () { this.saveKey() }
  },
  methods: {
    saveSettings () {
      try { localStorage.setItem(LS, JSON.stringify(this.settings)) } catch (e) { /* ignore */ }
    },
    loadKey () {
      const p = this.settings.provider
      let k = sessionKeys[p] || ''
      try { k = k || localStorage.getItem(keyName(p)) || '' } catch (e) { /* ignore */ }
      this.apiKey = k
    },
    saveKey () {
      const p = this.settings.provider
      if (!p || p === 'manual') return
      sessionKeys[p] = this.apiKey
      try {
        if (this.settings.remember && this.apiKey) localStorage.setItem(keyName(p), this.apiKey)
        else localStorage.removeItem(keyName(p))
      } catch (e) { /* ignore */ }
    },
    onProviderChange () {
      this.settings.model = ''
      this.modelTyped = ''
      this.settings.baseUrl = ''
      this.loadKey()
    },
    headers () {
      return this.apiKey ? { 'x-rb-ai-key': this.apiKey } : {}
    },
    aiParams () {
      // a model name that was typed but not yet confirmed with Enter still counts
      const model = (this.modelTyped || '').trim() || this.settings.model || undefined
      return { provider: this.settings.provider, model, baseUrl: this.settings.baseUrl || undefined }
    },
    async loadContext () {
      const res = await axios.get(`${this.base}/ai/context`)
      this.ctx = res.data.data
    },
    async rebuild () {
      this.rebuilding = true
      try {
        const res = await axios.post(`${this.base}/ai/context/rebuild`, {})
        this.ctx = res.data.data
        this.$snack('Assistant context rebuilt.', 'success')
      } finally {
        this.rebuilding = false
      }
    },
    async testConnection () {
      this.testing = true
      try {
        await axios.post(`${this.base}/ai/test`, { ai: this.aiParams() }, { headers: this.headers() })
        this.$snack('Connected - the AI service answered.', 'success')
      } finally {
        this.testing = false
      }
    },
    current () { return this.mode === 'edit' ? JSON.parse(JSON.stringify(state.tpl)) : undefined },
    remember () {
      const t = this.instruction.trim()
      this.history = [t, ...this.history.filter((h) => h !== t)].slice(0, 10)
    },
    async generate () {
      this.generating = true
      this.error = ''
      this.proposal = null
      try {
        const res = await axios.post(`${this.base}/ai/generate`, {
          instruction: this.instruction, mode: this.mode, current: this.current(), ai: this.aiParams()
        }, { headers: this.headers(), timeout: 300000 })
        this.remember()
        this.show(res.data.data)
      } catch (e) {
        this.error = errorText(e)
      } finally {
        this.generating = false
      }
    },
    async buildPrompt () {
      this.promptLoading = true
      this.error = ''
      try {
        const res = await axios.post(`${this.base}/ai/prompt`, { instruction: this.instruction, mode: this.mode, current: this.current() })
        this.promptText = res.data.data.text
        this.promptTokens = res.data.data.approxTokens
        this.remember()
        await this.copy(this.promptText)
      } catch (e) {
        this.error = errorText(e)
      } finally {
        this.promptLoading = false
      }
    },
    async readReply () {
      this.reading = true
      this.error = ''
      try {
        const res = await axios.post(`${this.base}/ai/read-reply`, { reply: this.pasted, current: this.current() })
        const d = res.data.data
        if (!d.ok) { this.error = d.problems.join(' '); return }
        this.show({ ...d, attempts: 1 })
      } catch (e) {
        this.error = errorText(e)
      } finally {
        this.reading = false
      }
    },
    async copy (text) {
      try {
        await navigator.clipboard.writeText(text)
        this.$snack('Copied to the clipboard.', 'success')
      } catch (e) {
        this.$snack('Could not copy automatically - select the text and copy it.', 'warning')
      }
    },
    show (d) {
      this.proposal = d
      const base = this.mode === 'edit' ? state.tpl : null
      this.changes = d.template && base ? diffTemplates(base, d.template) : []
      this.accepted = this.changes.map((c) => c.id)
    },
    kindColor (k) { return k === 'added' ? 'success' : k === 'removed' ? 'error' : 'info' },
    discard () { this.proposal = null; this.changes = []; this.accepted = [] },
    apply (all) {
      const next = this.proposal.template
      if (this.mode === 'new' || all) {
        if (this.mode === 'new' && state.id && !window.confirm('This replaces the report in the editor with the new one. Continue?')) return
        state.tpl = JSON.parse(JSON.stringify(next))
      } else {
        state.tpl = applyChanges(state.tpl, next, this.changes, this.accepted)
      }
      state.ctx = 'main'
      this.$snack('Applied to the editor. Check the Preview and Tests tabs, then save.', 'success')
      this.discard()
    }
  }
}
</script>

<style scoped>
.rb-secret ::v-deep input { -webkit-text-security: disc; text-security: disc; }
.rb-diff { background: rgba(128, 128, 128, 0.1); padding: 8px; border-radius: 4px; font-size: 12px; max-height: 300px; overflow: auto; white-space: pre-wrap; word-break: break-word; }
</style>
